import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogOut, Plus, Pencil, Trash2, Edit, Settings, HelpCircle } from 'lucide-react';
import GymnastsTab from '@/components/GymnastsTab';
import CoachesTab from '@/components/CoachesTab';
import JudgesTab from '@/components/JudgesTab';
import CompetitionsTab from '@/components/CompetitionsTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard({ user, club, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('gymnasts');
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [clubData, setClubData] = useState({ name: '', phone: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (club) {
      setClubData({ name: club.name, phone: club.phone });
    }
  }, [club]);

  const handleEditClub = () => {
    setClubData({ name: club.name, phone: club.phone });
    setShowEditDialog(true);
  };

  const handleSaveClub = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.put(`${API}/club/details`, clubData);
      toast.success('Club details updated successfully');
      setShowEditDialog(false);
      // Reload page to get updated club info
      window.location.reload();
    } catch (error) {
      console.error('Failed to update club:', error);
      toast.error(error.response?.data?.detail || 'Failed to update club details');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-3">
              <img src="/scorz-logo.png" alt="Scorz Logo" className="h-12 sm:h-16 w-auto" />
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
              <div className="text-left sm:text-right">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-black text-sm sm:text-base">{club?.name}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleEditClub}
                    className="text-[#000000] hover:bg-[#f5f5f5] p-1"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-gray-600 text-xs sm:text-sm">{club?.email}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setShowHelpDialog(true)}
                  variant="outline"
                  size="sm"
                  className="border-[#000000] text-[#000000] hover:bg-[#f5f5f5] whitespace-nowrap"
                >
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Help
                </Button>
                <Button
                  onClick={() => navigate('/settings')}
                  variant="outline"
                  size="sm"
                  className="border-[#000000] text-[#000000] hover:bg-[#f5f5f5] whitespace-nowrap"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </Button>
                <Button
                  data-testid="logout-button"
                  onClick={onLogout}
                  variant="outline"
                  size="sm"
                  className="border-[#000000] text-[#000000] hover:bg-[#f5f5f5] whitespace-nowrap"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 mb-6 sm:mb-8 bg-gray-100 h-auto p-1">
            <TabsTrigger
              value="gymnasts"
              data-testid="tab-gymnasts"
              className="data-[state=active]:bg-white data-[state=active]:text-[#000000] data-[state=active]:border-b-2 data-[state=active]:border-[#000000] text-xs sm:text-sm py-2"
            >
              Gymnasts
            </TabsTrigger>
            <TabsTrigger
              value="coaches"
              data-testid="tab-coaches"
              className="data-[state=active]:bg-white data-[state=active]:text-[#000000] data-[state=active]:border-b-2 data-[state=active]:border-[#000000] text-xs sm:text-sm py-2"
            >
              Coaches
            </TabsTrigger>
            <TabsTrigger
              value="judges"
              data-testid="tab-judges"
              className="data-[state=active]:bg-white data-[state=active]:text-[#000000] data-[state=active]:border-b-2 data-[state=active]:border-[#000000] text-xs sm:text-sm py-2"
            >
              Judges
            </TabsTrigger>
            <TabsTrigger
              value="competitions"
              data-testid="tab-competitions"
              className="data-[state=active]:bg-white data-[state=active]:text-[#000000] data-[state=active]:border-b-2 data-[state=active]:border-[#000000] text-xs sm:text-sm py-2"
            >
              Competitions
            </TabsTrigger>
          </TabsList>

          <TabsContent value="gymnasts">
            <GymnastsTab />
          </TabsContent>

          <TabsContent value="coaches">
            <CoachesTab />
          </TabsContent>

          <TabsContent value="judges">
            <JudgesTab />
          </TabsContent>

          <TabsContent value="competitions">
            <CompetitionsTab />
          </TabsContent>
        </Tabs>
      </main>

      {/* Edit Club Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Club Details</DialogTitle>
            <DialogDescription>
              Update your club information. Email and password cannot be changed here.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveClub} className="space-y-4">
            <div>
              <Label htmlFor="clubName">Club Name *</Label>
              <Input
                id="clubName"
                value={clubData.name}
                onChange={(e) => setClubData({ ...clubData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="clubPhone">Phone Number *</Label>
              <Input
                id="clubPhone"
                value={clubData.phone}
                onChange={(e) => setClubData({ ...clubData, phone: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Email (Cannot be changed)</Label>
              <Input value={club?.email} disabled className="bg-gray-100" />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditDialog(false)}
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#000000] hover:bg-[#333333]"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Help Dialog */}
      <Dialog open={showHelpDialog} onOpenChange={setShowHelpDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Need Help?</DialogTitle>
            <DialogDescription>
              Get support from the Scorz team
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Contact Support</h3>
              <p className="text-sm text-gray-600 mb-3">
                For technical issues or questions about the entry system, please email our support team:
              </p>
              <a
                href="mailto:support@scorz.co.uk"
                className="text-[#000000] hover:underline font-medium"
              >
                support@scorz.co.uk
              </a>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Common Topics</h3>
              <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                <li>Adding gymnasts, coaches, or judges</li>
                <li>Entering competitions</li>
                <li>Payment proof upload</li>
                <li>Managing team members with club codes</li>
                <li>Updating floor music</li>
              </ul>
            </div>
            <p className="text-xs text-gray-500">
              Our support team typically responds within 24 hours during business days.
            </p>
          </div>
          <Button
            onClick={() => setShowHelpDialog(false)}
            className="w-full bg-[#000000] hover:bg-[#333333]"
          >
            Close
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}